import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../store/rootReducer';
import {
    CommandBar,
    ICommandBarItemProps,
    IContextualMenuItem,
    ContextualMenuItemType, Callout, Text, Icon, getTheme,
    Dialog, DialogFooter, DialogType, PrimaryButton, DefaultButton
} from '@fluentui/react';
import {IWorkspace, IWorkspaceItem, IWorkspaceReference} from '../../models/Workspace';
import {WorkspaceDAL} from "../../dal/WorkspaceDAL";
import CustomizationModal from "../ColumnCustomization/CustomizationModal";
import {
    useGetInstalledExtensionsQuery,
    useGetRecentWorkspaceRefsQuery,

    useRemoveWorkspaceMutation,
    usePutWorkspaceMutation,

    useRunDocumentExtensionMutation,
    useLazyGetWorkspaceQuery
} from "../../store/Api";

import {
    useRemoveProductFromWorkspaceMutation,
} from "../../store/endpoints/WorkspaceItemsEndpoints";


import {IInstalledExtension} from "../../models/Extension";
import {setPunchoutWorkspace} from '../../store/settingsSlice';
import WorkspaceDeleteModal from "./WorkspaceDeleteModal";
import useCan, { ORG_EXTENSION_MODIFY, STOREFRONT_ADMIN } from '../../permissions/ability';
import {useUrlPanelState} from "../../hooks/useUrlState";
import ExtensionDetailsPanel from "../ExtensionInstallation/ExtensionDetailsPanel";
import ExtensionFolioDetailsPanel from "../ExtensionFolios/ExtensionFolioDetailsPanel";
import {useId} from "@fluentui/react-hooks";
import {useGetOrganizationEntitlementsQuery} from "../../store/endpoints/OrganizationsEndpoints";

interface IWorkspaceControlsProps {
    selectedWorkspace: string;
    /** Items the toolbar acts on for exports etc.; the page falls back to every line when nothing is checked. */
    selectedItems: IWorkspaceItem[];
    /** The user's explicit grid selection. Remove only ever acts on this, never on the fallback above. */
    removableItems?: IWorkspaceItem[];
    onExtensionSelection: (extension: IInstalledExtension) => void;
    onCustomerRequestSelection: () => void;
    onImportSelection: () => void;
    onRefresh?: () => void;
    onAdhocClick: () => void;
    onRefreshVendors?: () => void;
    onPrepareCart: () => void;
    refreshingVendors?: boolean;
    disableExtensions?: boolean;
    externalCommandBarProps?: ICommandBarItemProps[];
}
const WorkspaceControls: React.FC<IWorkspaceControlsProps> = props => {
    const dispatch = useDispatch();
    const getWorkspaces = useGetRecentWorkspaceRefsQuery();
    const [removeWorkspace] = useRemoveWorkspaceMutation();
    const [updateWorkspace] = usePutWorkspaceMutation();
    const selectedExtension = useSelector((state: RootState) => state.settings.targetExtension);
    const punchoutWorkspaceId = useSelector((state: RootState) => state.settings.punchoutWorkspace);
    const [selectedWorkspace, setSelectedWorkspace] = useState<string>();
    const [workspaceData, setWorkspaceData] = useState<IWorkspace>();
    const [getWorkspace] = useLazyGetWorkspaceQuery()
    const [deleteWorkspaceModalOpen, setDeleteWorkspaceModalOpen] = React.useState(false);
    const [recentWorkspaces, setRecentWorkspaces] = React.useState<IWorkspaceReference[] | undefined>([])
    const [removeProduct] = useRemoveProductFromWorkspaceMutation();
    const getInstalled = useGetInstalledExtensionsQuery();
    const [runDocumentExtension] = useRunDocumentExtensionMutation();
    const [documentGenerating, setDocumentGenerating] = useState<boolean>();
    const auth = useSelector((state: RootState) => state.auth);
    const settings = useSelector((state: RootState) => state.settings);
    const [submitUrl, setSubmitUrl] = React.useState<string | undefined>(undefined)
    const [submitData, setSubmitData] = React.useState<string | undefined>(undefined)
    const formRef = useRef<null | HTMLFormElement>(null);
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const getOrganizationEntitlements = useGetOrganizationEntitlementsQuery(selectedOrganization ?? '', {skip: !selectedOrganization || selectedOrganization === ''});
    const [editColumnsModalOpen, setEditColumnsModalOpen] = React.useState(false);
    const [, setLoadingWorkspace] = useState<boolean>();
    const [punchoutError, setPunchoutError] = React.useState<string | undefined>(undefined);
    const [showPunchoutError, setShowPunchoutError] = useState<boolean>(false);
    const canStorefront = useCan(STOREFRONT_ADMIN);
    const canEditExtensions = useCan(ORG_EXTENSION_MODIFY);
    // Same panel/instance URL keys as Extension Management, so the details panel's own
    // tab deep-linking (?panelTab=) keeps working when opened from the workspace.
    const extensionPanel = useUrlPanelState('extension');
    const folioPanel = useUrlPanelState('extensionFolio');
    const detailsExtension = React.useMemo(
        () => getInstalled.data?.find(e => e.id === (extensionPanel.instanceId ?? folioPanel.instanceId)),
        [getInstalled.data, extensionPanel.instanceId, folioPanel.instanceId]
    );
    const detailsIsFolio = detailsExtension?.extensionType === 'Folio';
    const detailsRequested = (extensionPanel.isOpen || folioPanel.isOpen) && !!detailsExtension;
    const errorIconId = useId('error-icon');
    const [submittingPunchout, setSubmittingPunchout] = React.useState<boolean>(false);
    useEffect(() => {
        if(!getWorkspaces.isFetching) {
            setRecentWorkspaces(getWorkspaces.data)
        }
    }, [getWorkspaces.data])
    useEffect(() => {
        setLoadingWorkspace(true)
        setSelectedWorkspace(props.selectedWorkspace)
        //Make this not lazy, otherwise we will miss attached company changes
        getWorkspace(props.selectedWorkspace).unwrap().then((ws) => {
            setWorkspaceData(ws)
            setLoadingWorkspace(false)
        })
    }, [props.selectedWorkspace])
    
    const toggleTemplate = (isTemplate: boolean) => {
        if(selectedWorkspace) {
            updateWorkspace({isTemplate: isTemplate, id: selectedWorkspace})
        }
    }
    // Remove only touches lines the user explicitly checked, and asks first: the toolbar's general
    // selection falls back to "every line" when nothing is checked, which must never mean "remove all".
    const removableItems = props.removableItems ?? [];
    const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
    const removeClick = () => {
        if (selectedWorkspace && removableItems.length > 0) setConfirmRemoveOpen(true);
    }
    const confirmRemove = () => {
        setConfirmRemoveOpen(false);
        if (!selectedWorkspace) return;
        for (const item of removableItems) {
            removeProduct({workspaceId: selectedWorkspace, wsItemId: item.id});
        }
    }
    const openDocument = () => {
        if(workspaceData?.documentUrl)
            window.open(workspaceData.documentUrl, '_blank')
    }
    const pluralized = (count: number, noun: string) => {
        return count >= 2 || count === 0 ? `${noun}s` : noun;
    }
    const submitSession = async () => {
        if (selectedWorkspace) {
            setSubmittingPunchout(true);
            setPunchoutError(undefined);
            setShowPunchoutError(false)
            const dal = new WorkspaceDAL(auth);
            if(settings.punchoutMode == "json") {
                const results = await dal.postSubmitJsonWorkspaceSession(selectedWorkspace);
                if(results?.success && results?.url){
                    cleanupPunchout();
                    window.open(results.url, "_self")
                }
                else{
                    setShowPunchoutError(true);
                    setPunchoutError(`Unable to submit punchout session. Error: ${results?.errorMessage}`);
                }
            } else {
                const results = await dal.postSubmitWorkspaceSession(selectedWorkspace);
                if(results && formRef?.current) {
                    setSubmitUrl(results.postUrl);
                    setSubmitData(results.data);
                    cleanupPunchout();
                    formRef?.current.submit();
                }
            }
            setSubmittingPunchout(false)
        }
    }

    const cleanupPunchout = () => {
        if (punchoutWorkspaceId != undefined) {
            removeWorkspace(punchoutWorkspaceId).then();
        }
        dispatch(setPunchoutWorkspace(undefined));
        // The recent list may be empty, or hold only the punchout workspace just removed.
        const next = recentWorkspaces?.find(w => w.id != punchoutWorkspaceId);
        if (next) setSelectedWorkspace(next.id);
    }
    // Actions on THIS workspace only. Switching workspaces, browsing more, and creating new ones live
    // on the header's Workspace split button.
    const workspaceEntries: IContextualMenuItem[] = [];
    workspaceEntries.push({
        key: 'deleteWorkspace',
        text: 'Delete',
        iconProps: {iconName: 'Delete'},
        onClick: () => setDeleteWorkspaceModalOpen(true),
    });
    workspaceEntries.push({
        key: 'customizeColumns',
        text: 'Customize Columns',
        iconProps: { iconName: 'edit'},
        onClick: () => setEditColumnsModalOpen(true),
    });
    workspaceEntries.push({
        key: 'import',
        text: 'Import File',
        iconProps: { iconName: 'OpenFile'},
        onClick: props.onImportSelection,
    })
    if (getOrganizationEntitlements.data?.documentAnalyzerEnabled) {
        workspaceEntries.push({
            key: 'customerRequest',
            text: 'Customer Request',
            iconProps: {iconName: 'ChatBot'},
            onClick: props.onCustomerRequestSelection,
        })
    }
    workspaceEntries.push({
        key: 'toggleTemplate',
        text: workspaceData?.isTemplate ? "Remove from Templates" : "Mark as Template",
        iconProps: { iconName: 'WebTemplate'},
        onClick: () => toggleTemplate(!(workspaceData?.isTemplate)),
    });
    if(canStorefront) {
        workspaceEntries.push({
            key: 'divider', 
            itemType: ContextualMenuItemType.Divider
        })
        workspaceEntries.push({
            key: 'addPreparedCart',
            text: 'Prepared Cart',
            iconProps: {iconName: 'ShoppingCartSolid'},
            onClick: props.onPrepareCart
        })
    }
    

    if (auth.embeddedSession) {
        workspaceEntries.push(
            {
                key: 'remove',
                text: removableItems.length === 0 ? 'Remove Products' : `Remove ${removableItems.length} ${pluralized(removableItems.length, 'Product')}`,
                disabled: removableItems.length === 0,
                iconProps:{ iconName: 'Delete'},
                onClick: removeClick,
            },
            {
                key: 'refreshVendors',
                text: props.refreshingVendors ? 'Refreshing' : 'Refresh Vendors',
                iconProps:{ iconName: 'Sync' },
                disabled: props.refreshingVendors,
                onClick: props.onRefreshVendors,
            },
            {
                key: 'refresh',
                text: 'Refresh',
                iconProps: {iconName: 'Refresh'},
                onClick: props.onRefresh,
            },
        )
    }

    const openExtensionDetails = (extension: IInstalledExtension) => {
        if (extension.isSubExtension) {
            // A sub-extension's configuration lives on its parent folio; its source is the folio id.
            const parentFolio = getInstalled.data?.find(e => e.extensionType === 'Folio' && e.source === extension.source);
            if (parentFolio) {
                folioPanel.open(parentFolio.id);
                return;
            }
        }
        if (extension.extensionType === 'Folio') folioPanel.open(extension.id);
        else extensionPanel.open(extension.id);
    }
    const closeExtensionDetails = () => {
        extensionPanel.close();
        folioPanel.close();
        getInstalled.refetch();
    }
    const extensionMenuItemContent = (extension: IInstalledExtension) => {
        const theme = getTheme();
        const isSelected = extension.id == selectedExtension;
        return (
            <div style={{display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0, padding: '0.5em 0'}}>
                {extension.image
                    ? <img
                        src={extension.image}
                        alt=''
                        style={{width: 24, height: 24, objectFit: 'contain', flexShrink: 0}}
                    />
                    // Keep rows aligned when an extension has no logo.
                    : <Icon iconName='PlugConnected' style={{width: 24, height: 24, fontSize: 16, lineHeight: '24px', textAlign: 'center', color: theme.palette.neutralTertiary, flexShrink: 0}} />}
                <div style={{flexGrow: 1, minWidth: 0, marginLeft: 8, textAlign: 'left'}}>
                    <div style={{color: isSelected ? 'rgb(107, 140, 42)' : undefined, lineHeight: '20px'}}>
                        {extension.name}
                    </div>
                    <div style={{
                        fontSize: 11,
                        lineHeight: '14px',
                        color: theme.palette.neutralSecondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}>
                        {extension.source}
                    </div>
                </div>
                {canEditExtensions &&
                    // A span, not an IconButton — menu rows are already <button>s and buttons can't nest.
                    <span
                        role='button'
                        title='Edit Extension'
                        data-is-focusable={false}
                        style={{padding: '0 4px', cursor: 'pointer', color: theme.palette.neutralSecondary}}
                        onClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            openExtensionDetails(extension);
                        }}
                    >
                        <Icon iconName='Edit' />
                    </span>}
            </div>
        );
    }
    const availableExtensionMenuItems = (possibleExtensions : IInstalledExtension[]) => {
        return possibleExtensions?.sort((x,y) => {
            return x.id == selectedExtension ? -1 : y.id == selectedExtension ? 1 : 0;
        }).
        map((extension) => ({
                key: extension.id,
                text: extension.name,
                // An empty workspace still has use for imports; only extensions that act on the
                // workspace's items are disabled without any.
                disabled: props.disableExtensions && extension.extensionType !== 'directImport',
                // The two-line content outgrows the fixed 36px menu row.
                itemProps: {styles: {root: {height: 'auto'}}},
                onRenderContent: () => extensionMenuItemContent(extension),
                onClick: () => props.onExtensionSelection(extension)
            })
        );
    }
    const shouldOfferRegeneration = () => {
        return (workspaceData?.documentIsCurrent != true && workspaceData?.mostRecentDocumentExtension);
    }


    const getCommands = () => {
        const commands: ICommandBarItemProps[] = []
        const documentExtensions = getInstalled.data?.filter(e => e.enabled == true && e.extensionType === 'document');
        const availableExtensions = getInstalled.data?.filter((e => 
            e.enabled && 
            (e.extensionType === 'export' || e.extensionType === 'recommendation') || e.extensionType === 'directImport'));
        if (auth.embeddedSession) {
            commands.push(
                {
                    key: 'extensions',
                    text: 'Extensions',
                    subMenuProps: {
                        items: availableExtensionMenuItems(availableExtensions ?? []) ?? [],
                    }
                },
                {
                    key: 'workspace',
                    text: 'Manage',
                    iconProps: {iconName: 'Settings'},
                    subMenuProps: {
                        items: workspaceEntries,
                    }
                },
            )
        } else {
            commands.push({
                key: 'remove',
                text: removableItems.length === 0
                    ? 'Remove products'
                    : `Remove ${removableItems.length} ${pluralized(removableItems.length, 'product')}`,
                title: removableItems.length === 0 ? 'Select products in the grid to remove them' : undefined,
                disabled: removableItems.length === 0,
                iconProps:{ iconName: 'Delete'},
                onClick: removeClick
            })
            commands.push({
                key: 'refreshVendors',
                text: props.refreshingVendors ? 'Refreshing' : 'Refresh Vendors',
                iconProps:{ iconName: 'Sync' },
                disabled: props.refreshingVendors,
                onClick: props.onRefreshVendors
            })
            if (getOrganizationEntitlements.data?.adHocEnabled) {
                commands.push({
                    key: 'adhoc',
                    iconOnly: true,
                    iconProps: {iconName: 'Add'},
                    tooltipHostProps: {
                        content: "Create Ad-Hoc Workspace Item",
                        calloutProps: {gapSpace: 0},
                        styles: {root: {display: 'inline-block'}}
                    },
                    onClick: props.onAdhocClick
                })
            }
            if(workspaceData?.documentUrl){
                commands.push({
                    key: 'viewDocument',
                    text: 'View Document',
                    onClick: openDocument
                })
            }
            if (documentExtensions && documentExtensions.length > 0) {
                const items: IContextualMenuItem[] = [{
                    key: "sectionTitle",
                    text: "Document Extensions",
                    itemType: ContextualMenuItemType.Header
                }, ...documentExtensions.map((ext) => (
                    {
                        key: ext.id, 
                        text: ext.name,
                        onClick: (a: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement> | undefined, b: IContextualMenuItem | undefined) => {
                            setDocumentGenerating(true)
                            runDocumentExtension({ extensionId: b?.key ?? "", workspaceId: workspaceData?.id ?? ""})
                                .then(() => setDocumentGenerating(false))
                        }
                    }))]
                if(shouldOfferRegeneration()) {
                    commands.push({
                        key: 'documents',
                        text: documentGenerating ? 'Document Refreshing' : 'Refresh Document',
                        split: true,
                        onClick: () => {
                            setDocumentGenerating(true)
                            runDocumentExtension({
                                extensionId: workspaceData?.mostRecentDocumentExtension ?? "",
                                workspaceId: workspaceData?.id ?? ""
                            }).then(() => setDocumentGenerating(false))
                        },
                        disabled: documentGenerating,
                        subMenuProps: {
                            items: [...items],
                        },
                        iconProps: {iconName: 'Refresh'}

                    })
                }
                else {
                    commands.push({
                        key: 'documents',
                        text: documentGenerating ? 'Document Refreshing' : 'Documents',
                        subMenuProps: {
                            items: [...items],
                        },
                        disabled: documentGenerating,
                        iconProps: {iconName: 'TextDocument'}
                    })
                }
            }
            if (auth.sessionKey != null) {
                commands.push({
                    key: 'Submit',
                    disabled: submittingPunchout,
                    text: `${submittingPunchout? 'Submitting...' : 'Submit'}`,
                    onClick: () => {
                        submitSession();
                    }
                })
            } else if(availableExtensions && availableExtensions.length > 0) {
                commands.push({
                    key: 'extensions',
                    // The menu stays reachable on an empty workspace when it holds any import-capable
                    // extension; the item-dependent entries inside it disable individually.
                    disabled: props.disableExtensions && !availableExtensions.some(e => e.extensionType === 'directImport'),
                    text: 'Extensions',
                    subMenuProps: {
                        items: availableExtensionMenuItems(availableExtensions) ?? [],
                    }
                });
            }
            if(punchoutError) {
                commands.push({
                    key: 'errors',
                    id: errorIconId,
                    onRenderIcon: () => <Icon
                        iconName={'Warning'}
                        style={{color: '#ff0000'}}
                    />,
                    iconOnly: true,
                    onClick: () => setShowPunchoutError(true)
                });
            }
            commands.push(
                {
                    key: 'workspace',
                    // Actions on the open workspace (delete, columns, import, template); switching and
                    // creating workspaces live on the header's Workspace split button.
                    text: 'Manage',
                    iconProps: {iconName: 'Settings'},
                    subMenuProps: {
                        items: workspaceEntries,
                    }
                },
                {
                    key: 'refresh',
                    text: 'Refresh',
                    iconProps: {iconName: 'Refresh'},
                    onClick: props.onRefresh,
                    iconOnly: true,
                }
            )
        }

        return commands
    }
    
    return (
        <>
            <form ref={formRef} id="cxml_form" method="POST" action={submitUrl} encType="application/x-www-form-urlencoded">
                <input type="hidden" name="cXML-urlencoded" value={submitData} />
            </form>
            {showPunchoutError && <Callout
                target={`#${errorIconId}`}
                onDismiss={() => setShowPunchoutError(false)}
                setInitialFocus
                style={{padding: '1em', backgroundColor: '#f8d1d1'}}
                styles={{
                    beak: { backgroundColor: '#f8d1d1' },
                }}
            >
                <Text as='h1' block variant={'xLarge'}>There was an error</Text>
                <Text key={'jsonPunchoutError'} as='p' block variant={'mediumPlus'}>Punchout submission failed with error: {punchoutError}</Text>
                
            </Callout>}
            {/* Only primary items can overflow, and the only primary item is the page's Bundles toggle,
                which must stay visible; so the bar never reduces. */}
            <CommandBar items={props.externalCommandBarProps ?? []} farItems={getCommands()} onReduceData={() => undefined}/>            
            <Dialog
                hidden={!confirmRemoveOpen}
                onDismiss={() => setConfirmRemoveOpen(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: `Remove ${removableItems.length} ${pluralized(removableItems.length, 'product')}?`,
                    subText: 'The selected lines are removed from this workspace. This cannot be undone.'
                }}
                modalProps={{isBlocking: true}}>
                <DialogFooter>
                    <PrimaryButton text='Remove' iconProps={{iconName: 'Delete'}} onClick={confirmRemove} />
                    <DefaultButton text='Cancel' onClick={() => setConfirmRemoveOpen(false)} />
                </DialogFooter>
            </Dialog>
            <WorkspaceDeleteModal
                displayModal={deleteWorkspaceModalOpen}
                selectedWorkspace={selectedWorkspace}
                onDeleteWorkspace={() => {
                    if(selectedWorkspace && selectedWorkspace != "" && selectedWorkspace == punchoutWorkspaceId){
                        dispatch(setPunchoutWorkspace(undefined));
                    }
                    // The recent list may be empty, or hold only the workspace just deleted.
                    setSelectedWorkspace(recentWorkspaces?.find(w => w.id != selectedWorkspace)?.id)

                }}
                closer={() => setDeleteWorkspaceModalOpen(false)}
            />
            <CustomizationModal
                page='workspace'
                isOpen={editColumnsModalOpen}
                onClose={() => setEditColumnsModalOpen(false)}
            />
            {canEditExtensions && <>
                <ExtensionDetailsPanel
                    installedExtension={detailsExtension}
                    extensionId=''
                    isOpen={detailsRequested && !detailsIsFolio}
                    onClose={closeExtensionDetails}
                    isNew={false}
                />
                <ExtensionFolioDetailsPanel
                    installedExtension={detailsExtension}
                    extensionId=''
                    isOpen={detailsRequested && detailsIsFolio}
                    onClose={closeExtensionDetails}
                    isNew={false}
                />
            </>}
        </>
    )
}

export default WorkspaceControls;